// ──────────────────────────────────────────────────────────────────────
// Lembra product catalogue — single source of truth.
//
// This file is imported by BOTH the client (via the `@shared` Vite alias
// mapping to supabase/functions/_shared) AND the Supabase edge functions
// (via direct relative import). Do not add React, Deno-only, or
// browser-only APIs here — keep it plain TypeScript so both runtimes
// can consume it.
//
// ── Variation C — locked 27 Apr 2026 ──
// 3 customer-facing size bands (Team / Floor / Office) + Big Days
// concierge tier for 36+ headcount events. Pricing and COGS verified
// against live bakery products.json feeds (April 2026 audit).
//
// Bakery curation rule: each bakery only ships products it's actually
// known for.
//   - Goldelucks  → donuts only (no cakes, no cookies, no brownies)
//   - Cupcake Central + Little Cupcakes → cupcakes anchor + cookies,
//                                         brownies, classic cakes
//   - Le Yeahllow + Chéri → full premium catalogue
//
// Contextual filter: kid-themed cakes, holiday-locked SKUs, and
// occasion-specific seasonal SKUs are surfaced only in the Big Days
// concierge flow — not in the main 3×3 grid.
// ──────────────────────────────────────────────────────────────────────

/* ─── Core enumerations ─── */

export const CELEBRATION_TYPES = [
  "birthday",
  "work_anniversary",
  "welcome",
  "farewell",
  "milestone",
  "other",
] as const;
export type CelebrationType = typeof CELEBRATION_TYPES[number];

export const TEAM_SIZES = ["team", "floor", "office", "big_days"] as const;
export type TeamSizeBucket = typeof TEAM_SIZES[number];

export const TIER_NAMES = ["Standard", "Premium", "Luxury"] as const;
export type TierName = typeof TIER_NAMES[number];

export const VIBES = ["fun", "elegant", "luxury"] as const;
export type Vibe = typeof VIBES[number];

export const BAKERIES = [
  "Cupcake Central",
  "Little Cupcakes",
  "Goldelucks",
  "Le Yeahllow",
  "Chéri",
] as const;
export type BakeryName = typeof BAKERIES[number];

/* ─── Display labels ─── */

// Customer-facing tier labels. Internal TIER_NAMES stay stable so DB
// records and Stripe validation keep working — only the display swaps.
export const TIER_DISPLAY_NAMES: Record<TierName, string> = {
  Standard: "Sweet & Fun",
  Premium: "Bit Fancy",
  Luxury: "Go All Out",
};

/* ─── Bakery × tier × vibe mappings ───
 * BAKERY_TIER records each bakery's HOME tier — i.e. where it appears
 * as the default in the picker. The runtime algorithm may pull across
 * tiers for some compositions (e.g. Bit Fancy at Floor uses both LY
 * and Chéri depending on headcount), but for picker UX this is the
 * primary anchor.
 */

export const BAKERY_TIER: Record<BakeryName, TierName> = {
  "Cupcake Central": "Standard",
  "Little Cupcakes": "Standard",
  "Goldelucks": "Standard",
  "Le Yeahllow": "Premium",
  "Chéri": "Luxury",
};

export const TIER_TO_VIBE: Record<TierName, Vibe> = {
  "Standard": "fun",
  "Premium": "elegant",
  "Luxury": "luxury",
};

/* ─── Team size (string → bucket) map ───
 * Normalises the various legacy/new string forms stored on
 * companies.team_size into the canonical TeamSizeBucket enum. The
 * 5×3 → 3×3 migration collapses very_small + small into "team",
 * remaps medium → floor, large → office, very_large → big_days.
 *
 * Existing records with the legacy enum strings continue to resolve
 * cleanly; new records should use the canonical band strings
 * ("5-12" / "13-22" / "23-35" / "36+").
 */
export const TEAM_SIZE_MAP: Record<string, TeamSizeBucket> = {
  // Canonical (new V-C grid)
  "5-12":  "team",
  "13-22": "floor",
  "23-35": "office",
  "36+":   "big_days",

  // Legacy enum strings (5×3 model)
  "very_small": "team",
  "small":      "team",
  "medium":     "floor",
  "large":      "office",
  "very_large": "big_days",

  // Legacy band strings
  "2-8":     "team",
  "8-15":    "team",
  "15-25":   "floor",
  "25-35":   "office",
  "36-200":  "big_days",
  "1-10":    "team",
  "10-15":   "team",
  "11-25":   "floor",
  "26-50":   "big_days",
  "51-100":  "big_days",
  "100+":    "big_days",
};

/* ─── Team size catalogue (labels for the UI) ─── */

export const TEAM_SIZE_OPTIONS: { key: TeamSizeBucket; label: string; range: string }[] = [
  { key: "team",     label: "Team",     range: "5–12 people"  },
  { key: "floor",    label: "Floor",    range: "13–22 people" },
  { key: "office",   label: "Office",   range: "23–35 people" },
  { key: "big_days", label: "Big Days", range: "36+ people"   },
];

/* ─── Pricing table ───
 * Keyed by team size × vibe. null = not offered at that combo. Big
 * Days routes to a concierge quote flow for every vibe (see
 * BIG_DAYS_STARTING_PRICE for the headline "from $X" anchor).
 *
 * Variation C grid locked 27 April 2026 — see
 * lembra-pricing-thesis-2026-04-27.md.
 */
export const PRICING: Record<TeamSizeBucket, Record<Vibe, number | null>> = {
  team:     { fun: 99,  elegant: 169, luxury: 219 },
  floor:    { fun: 179, elegant: 299, luxury: 399 },
  office:   { fun: 289, elegant: 449, luxury: 599 },
  big_days: { fun: null, elegant: null, luxury: null },
};

// Headline "from $X" anchor for landing pages and the Big Days CTA
// before headcount is entered. Approximates the cheapest concierge
// composition (CC 60-pack mini catering at retail) — actual charge is
// set after the bracket algorithm runs.
export const BIG_DAYS_STARTING_PRICE = 329;

// Backwards-compat alias so existing `import { VL_STARTING_PRICE }`
// callers don't break during the rebuild rollout. Prefer
// BIG_DAYS_STARTING_PRICE in new code.
export const VL_STARTING_PRICE = BIG_DAYS_STARTING_PRICE;

/* ─── Treat prices per bakery (for server-side validation) ───
 * The superset of valid per-treat retail prices. The client exposes
 * the richer TreatOption objects (name, description, image, etc.)
 * from src/components/order/orderData.ts; the edge function only
 * needs the bare price list to verify the `packagePrice` field isn't
 * tampered with at checkout. Run `bun run check:prices` to diff this
 * against the client TREAT_MENUS.
 *
 * All COGS verified against live bakery products.json feeds, April
 * 2026 audit. Prices are bakery retail (what Lembra pays); customer
 * retail is the PRICING grid above.
 */
export const TREAT_PRICES: Record<BakeryName, number[]> = {
  // CC: cupcakes (gift, custom-logo, mini, catering) + cookies +
  // brownies + scones + classic cakes + naked cakes + photo cakes
  "Cupcake Central": [
    29, 33, 36, 45, 48, 52.5, 57, 58, 60, 65, 66, 68, 72, 75, 76, 79,
    88, 89, 92, 95, 96, 98, 115, 120, 130, 165, 168, 175,
  ],

  // LC: cupcakes (assorted, mini, corporate, vegan) + classic cakes +
  // personalised message cake + edible image corporate cake + cupcake
  // bouquet + premium White & Gold
  "Little Cupcakes": [
    36, 48, 60, 65, 72, 85, 96, 120, 125, 132, 144, 192, 240, 320,
  ],

  // GL: donuts only (party box 9–255, personalised, letter, bouquet,
  // donut cakes 25–30cm, lolly box). Layer cakes / brownies / cookies
  // explicitly excluded per founder bakery-specialization rule.
  // Pricing audit 2026-05-08: $195 added for the 45-pack tier — fills
  // the office-band gap between 30-pack ($138) and the big_days 60-pack
  // ($260). $79 (Build Your Own Donuts — 15 Pack) was added the same
  // day as the year-round bouquet substitute, then removed when we
  // dropped gl-bouquet (no UI to actually let a customer pick a BYO
  // mix yet — see the donut-builder scope doc on the gl-bouquet
  // removal PR). $85 stays in the whitelist for gl-large-lolly.
  "Goldelucks": [
    55, 65, 69, 75, 85, 138, 150, 195, 207, 260, 295, 350, 400, 455, 460,
    510, 560, 660, 715, 770, 1105,
  ],

  // LY: full premium catalogue — whole cakes (5"–11"), petit gateau
  // ($15.50–$17.50), macarons, pastry boxes (4pc / 8pc), Breton tin,
  // lemon tea cake (room-temp stable), Balloon Puppy.
  // 30 Apr 2026: $29 already valid (Macaron Box 8pc COGS); new Pastry
  // Gift Box (4pc) shares the price tier — added explicitly for
  // server-side validation of Path B bundle composition.
  "Le Yeahllow": [
    15.5, 16, 16.5, 17.5, 29, 39, 45, 50, 59, 75, 79, 85, 90, 95, 99,
    105, 109, 115, 125, 135, 139, 165, 170, 209,
  ],

  // Chéri: Grand Gâteaux + whole tarts + premium 6pc dessert boxes +
  // mid 6pc + entry pastry 6pc + 7 individual cake 6pc + cups +
  // savoury catering (croissants, quiche, pizzas, piadinas, baguettes)
  "Chéri": [
    35, 36, 40, 42, 45, 48, 54, 60, 66, 72, 78, 84, 95, 96, 99, 100,
    108,
  ],
};

/* ─── Unified Product schema (Phase C, 2026-05-07) ───
 *
 * Single source of truth for every selectable item across both flows:
 * the "treat" picker AND the add-ons step. Each product declares its
 * `surfaces[]` — `["treat"]`, `["addon"]`, or `["treat", "addon"]` —
 * so the structural guarantee "one product, one price, multiple
 * surfaces" is enforced at the schema level rather than hoped-for at
 * the data level.
 *
 * Why this exists: prior to this refactor, ADD_ONS and TREAT_MENUS
 * were independent tables. Same products kept slipping in at both
 * surfaces with mismatched prices — Letter Donuts (Goldelucks) and
 * the four Chéri whole tarts each surfaced once as a $69/$72 treat
 * AND as a $74/$77 "Premium box presentation" add-on, letting a
 * customer pick the same dessert TWICE in a single order at a $5
 * upcharge they never agreed to. The fix isn't catching the dupes
 * after the fact (we did that on 2026-05-07 — see comments in the
 * pre-refactor commit log), it's making the schema reject them.
 *
 * Current scope (Phase C, this PR):
 *   - All bakery add-ons migrated to PRODUCTS with surfaces=["addon"]
 *   - ADD_ONS (the existing per-bakery export) is now derived from
 *     PRODUCTS — back-compat kept for every consumer (StepAddOns,
 *     StepReviewPay, getAddOnImage, etc.)
 *   - validateProductsCatalogue() runs at module load and throws if
 *     PRODUCTS contains structural defects (duplicate ids, missing
 *     surface-specific fields, conflicting surfaces on a single id)
 *
 * Out of scope (Phase D / later):
 *   - Migrating client TREAT_MENUS into PRODUCTS. RAW_TREAT_MENUS
 *     carries rich UI fields (description, contents, image,
 *     assortmentOptions, bundleExtras) that don't belong server-side.
 *     A future migration will keep the rich client view but
 *     cross-reference PRODUCTS by id for price-of-truth — TREAT_PRICES
 *     becomes derivable from PRODUCTS once that lands.
 *   - Cross-surface entries (surfaces=["treat", "addon"]). The schema
 *     accepts them today, but no current product needs both surfaces.
 *     The Chéri "Premium presentation box" upcharge that was the
 *     original motivation for cross-surface support is queued for the
 *     same Phase D migration as a `BundleUpgrade` schema instead.
 */

export const PRODUCT_SURFACES = ["treat", "addon"] as const;
export type ProductSurface = typeof PRODUCT_SURFACES[number];

export interface Product {
  /** Stable id, lower-snake-case with a bakery prefix
   *  (e.g. "ly-macarons", "ch-vovo"). Used as the React key on cards
   *  and as the `id` field on add-ons rows in orders. */
  id: string;
  /** Bakery that ships this product. */
  bakery: BakeryName;
  /** Display name shown to the customer. */
  name: string;
  /** Customer-facing description (1 sentence). */
  description: string;
  /** Bakery retail in AUD. ONE PRICE PER PRODUCT — surfaces share it. */
  price: number;
  /** Where this product appears in the order flow. At least one. */
  surfaces: ProductSurface[];
  /** Optional per-product image URL — used by both surfaces. */
  image?: string;

  /* ── Add-on-only fields (required when surfaces.includes("addon")) ── */

  /** Emoji fallback when the client has no per-product image. */
  addonEmoji?: string;
  /** "celebrant" (gift for the person) or "table" (group extra). */
  addonCategory?: "celebrant" | "table";
  /** Human-readable tier/bakery scope, surfaced in add-on tooltips. */
  availableWith?: string;

  /* ── Treat-only fields (required when surfaces.includes("treat")) ── */

  /** Picker category — "Cupcakes", "Cakes", "Donuts", "Pastry", etc. */
  treatCategory?: string;
  /** Customer-visible serves estimate ("12–15"). Treat-side only. */
  treatServes?: string;
}

/* ─── Back-compat: the legacy CatalogueAddOn shape ───
 * Existing consumers (orderData re-export, edge functions, tests)
 * import { CatalogueAddOn } from this file. We keep that shape stable
 * and derive ADD_ONS from PRODUCTS at module load — no consumer needs
 * to change.
 */
export interface CatalogueAddOn {
  id: string;
  name: string;
  description: string;
  price: number;
  /** Emoji fallback when the client doesn't have a per-product image. */
  emoji: string;
  /** Optional per-product image URL (client-only concern). */
  image?: string;
  /** "celebrant" (gift for the person) or "table" (extras for the group). */
  category: "celebrant" | "table";
  /** Human-readable tier/bakery scope, surfaced in tooltips. */
  availableWith: string;
}

/* ─── PRODUCTS — single source of truth ───
 *
 * Curated under bakery-specialization rule (see file header):
 *   Goldelucks → donut-format add-ons only
 *   CC + LC    → cupcakes anchor + cookies/brownies/scones/cakes
 *   LY + Chéri → full premium range
 *
 * DELIBERATELY OMITTED add-ons:
 *   - "Personalised Card" — bundled in every package
 *     (PACKAGE_CONTENTS in client orderData.ts), so offering as a paid
 *     upsell would double-charge.
 *   - "Birthday Candles" — bundled into birthday packages at every
 *     tier as of 2026-05-07 (see getEffectivePackageContents). Resolves
 *     Pricing & Economics Open Q7.
 *
 * REMOVED 2026-05-07 (treat+addon collisions):
 *   - GL Letter Donuts (was at $69 in both TREAT_MENUS and ADD_ONS)
 *   - Chéri whole tarts × 3 (Chocolate / Lemon Meringue / Strawberry
 *     Pistachio) and Pistachio Financier 6pc — were at $74/$77 in
 *     ADD_ONS as "Premium presentation box" and at $69/$72 in
 *     TREAT_MENUS. The gift-box upcharge will return as a BundleUpgrade
 *     under Phase D (separate from the Product itself).
 */
export const PRODUCTS: Product[] = [
  /* ── Cupcake Central ──
   * COGS verified — cupcakecentral.com.au products.json (April 2026). */
  // CC mini box: pricing audit 2026-05-08. Real SKU on
  // cupcakecentral.com.au is the 15-pack at $45 (Devil's Food, Vanilla
  // Vanilla, Assorted GF, etc.) — there is no 12-pack mini. Display
  // name corrected from "12 Mini Cupcake Box" → "15 Mini Cupcake Box";
  // price unchanged ($45 was already the real 15-pack price). The id
  // (`cc-mini-12`) stays as a legacy handle since changing it would
  // invalidate any existing order rows that reference it; the digit
  // in the id has no customer-facing exposure.
  { id: "cc-mini-12",    bakery: "Cupcake Central", name: "15 Mini Cupcake Box",         description: "15 mini cupcakes — extra crowd extension",     price: 45, surfaces: ["addon"], addonEmoji: "🧁",  addonCategory: "table",     availableWith: "Sweet & Fun (CC)" },
  { id: "cc-cookies",    bakery: "Cupcake Central", name: "12 Cookie Gift Box",          description: "Chunky filled cookies — assorted flavours",    price: 72, surfaces: ["addon"], addonEmoji: "🍪",  addonCategory: "table",     availableWith: "Sweet & Fun (CC)" },
  { id: "cc-brownies",   bakery: "Cupcake Central", name: "12 Brownie & Slice Box",      description: "Assorted fudgy brownies and Aussie slices",    price: 76, surfaces: ["addon"], addonEmoji: "🍫",  addonCategory: "table",     availableWith: "Sweet & Fun (CC)" },
  { id: "cc-shortbread", bakery: "Cupcake Central", name: "12 Custom Shortbread (logo)", description: "Personalised shortbread — ships AU-wide",      price: 65, surfaces: ["addon"], addonEmoji: "🪪",  addonCategory: "table",     availableWith: "Sweet & Fun (CC)" },
  { id: "cc-scones-12",  bakery: "Cupcake Central", name: "12 Scone Box w/ Jam & Cream", description: "Scones with raspberry jam and chantilly cream", price: 57, surfaces: ["addon"], addonEmoji: "🥮",  addonCategory: "table",     availableWith: "Sweet & Fun (CC)" },
  { id: "cc-coolbag",    bakery: "Cupcake Central", name: "Re-usable Cool Bag",          description: "Insulated bag — keeps items chilled 4 hrs",    price: 10, surfaces: ["addon"], addonEmoji: "🧊",  addonCategory: "table",     availableWith: "All tiers" },

  /* ── Little Cupcakes ── */
  { id: "lc-bouquet",  bakery: "Little Cupcakes", name: "Cupcake Bouquet (25–30 minis)", description: "Mini cupcake bouquet — beautifully arranged",   price: 125,  surfaces: ["addon"], addonEmoji: "💐",  addonCategory: "table",     availableWith: "Sweet & Fun (LC)" },
  { id: "lc-vegan-12", bakery: "Little Cupcakes", name: "12 Vegan Corporate Cupcakes",   description: "Plant-based cupcakes with branded packaging",   price: 72,   surfaces: ["addon"], addonEmoji: "🌱",  addonCategory: "table",     availableWith: "Sweet & Fun (LC)" },
  { id: "lc-topper",   bakery: "Little Cupcakes", name: "Happy Birthday Topper",         description: "Acrylic 'Happy Birthday' cake topper",          price: 4.9,  surfaces: ["addon"], addonEmoji: "✨",  addonCategory: "celebrant", availableWith: "Sweet & Fun (LC)" },

  /* ── Goldelucks ──
   * Donuts-only per founder bakery-specialization rule. Phantom Loaded
   * Brownie ($89) and Explosion Box ($99) confirmed not on catalogue
   * (April 2026 audit) — never make it back.
   *
   * gl-bouquet REMOVED 2026-05-08. The audit confirmed the only $85
   * "bouquet" SKU on goldelucks.com.au is the Mother's Day Heart
   * Bouquet, with Galentine's / Valentine's variants rotating in/out
   * of the catalogue. We previously kept it as a year-round add-on
   * with a "(seasonal)" caveat; founder direction same day: a SKU
   * that's only orderable a few weeks of the year shouldn't be a
   * permanent customer-facing line. Either it's available, in which
   * case it doesn't need a seasonality caveat, or it's not, in which
   * case it shouldn't surface. Removed.
   *
   * If we want a year-round equivalent of the heart bouquet, the
   * follow-up is to build a build-your-own donut flow (mirror of the
   * D6 CC build-your-own pattern) on top of the GL Donut Party Box
   * (15pk) — see the scoping doc in the PR that ships this comment.
   * Until that lands, the customer's GL bouquet path is "no fixed
   * SKU, but the picker has the 15pk for them to start from". */
  { id: "gl-large-lolly", bakery: "Goldelucks", name: "Large Donut & Lolly Box", description: "16 donuts plus mixed retro Aussie lollies",        price: 85, surfaces: ["addon"], addonEmoji: "🍬", addonCategory: "table", availableWith: "Sweet & Fun (GL)" },

  /* ── Le Yeahllow ──
   * COGS verified — shop.leyeahllow.com.au products.json (April 2026).
   * Phantom Canelé Box 16pc ($99/$129) confirmed not on catalogue —
   * never reintroduce.
   * 🥮 (moon cake) is the closest Unicode glyph to a macaron;
   * 🍬 (candy/wrapped sweet) misrepresents the product. */
  { id: "ly-macarons", bakery: "Le Yeahllow", name: "Macaron Box (8pc)",                description: "8 handcrafted French macarons across 8 flavours",                price: 29, surfaces: ["addon"], addonEmoji: "🥮", addonCategory: "table",     availableWith: "Bit Fancy (LY)" },
  // Path B (30 Apr 2026): the 4pc box at $29 keeps Bit Fancy Team's
  // $169 anchor honest on 5"-cake bundles without breaching margin
  // further than the 8pc would. Surfaced as a standalone add-on AND
  // auto-bundled via TreatOption.bundleExtras on smallest-size LY cakes.
  // Pricing audit 2026-05-08: $29 confirmed against shop.leyeahllow.com.au
  // products.json. Note: SKU is currently flagged SOLD OUT on the live
  // catalogue — when LY restocks, this entry resumes serving as both a
  // standalone add-on and the auto-bundled extra on 5" cakes. Ops should
  // swap to the 8pc ($50) as a workaround if a 5"-cake order lands during
  // a stockout, or fall back to the $25 ly-card upgrade if both pastry
  // sizes are out.
  { id: "ly-pastry-4", bakery: "Le Yeahllow", name: "Pastry Gift Box (4pc)",            description: "4 premium French pastries — chef's selection",                   price: 29, surfaces: ["addon"], addonEmoji: "🥐", addonCategory: "table",     availableWith: "Bit Fancy (LY) — bundled with 5\" cakes" },
  { id: "ly-pastry",   bakery: "Le Yeahllow", name: "Pastry Gift Box (8pc)",            description: "8 premium French pastries — chef's selection",                   price: 50, surfaces: ["addon"], addonEmoji: "🥐", addonCategory: "table",     availableWith: "Bit Fancy (LY)" },
  { id: "ly-breton",   bakery: "Le Yeahllow", name: "French Breton Cookies Tin (32pc)", description: "Buttery Breton sablé cookies in a keepsake tin",                 price: 59, surfaces: ["addon"], addonEmoji: "🍪", addonCategory: "table",     availableWith: "Bit Fancy (LY)" },
  { id: "ly-coolbag",  bakery: "Le Yeahllow", name: "Cool Bag (fresh-cream cakes)",     description: "Insulated cool bag — required for fresh-cream cakes in summer", price: 13, surfaces: ["addon"], addonEmoji: "🧊", addonCategory: "table",     availableWith: "Bit Fancy (LY)" },
  // The Luxury Gift Card UPGRADE sits on top of the bundled
  // Personalised Card (foil-pressed, hardcover finish). Distinct from
  // the bundled card itself. Re-added after the 22 Apr 2026 catalogue
  // rewrite where the SKU was inadvertently dropped (caused checkout
  // outage) — guarded by tests below.
  { id: "ly-card",     bakery: "Le Yeahllow", name: "Luxury Gift Card upgrade",         description: "Upgrade the bundled card to a foil-pressed premium edition",     price: 25, surfaces: ["addon"], addonEmoji: "✉️", addonCategory: "celebrant", availableWith: "Bit Fancy (LY)" },
  { id: "ly-plaque",   bakery: "Le Yeahllow", name: "Chocolate Plaque (custom message)", description: "25-character custom message piped on chocolate",                price: 7,  surfaces: ["addon"], addonEmoji: "✏️", addonCategory: "celebrant", availableWith: "Bit Fancy (LY)" },
  { id: "ly-serving",  bakery: "Le Yeahllow", name: "Cake Serving Set (5pc)",            description: "Knife, forks and plates for 5",                                  price: 15, surfaces: ["addon"], addonEmoji: "🍴", addonCategory: "table",     availableWith: "Bit Fancy (LY)" },

  /* ── Chéri ──
   * COGS verified — order.cherieatery.com.au products.json (April
   * 2026). Iced Vovo is the only true add-on remaining — the four
   * dessert SKUs that previously lived here as "Premium box
   * presentation" upcharges (Chocolate Tart, Lemon Meringue Tart,
   * Strawberry Pistachio Tart, Pistachio Financier 6pc) duplicated
   * the same name in TREAT_MENUS at the bare price. Phase D will
   * reintroduce the gift-box upcharge as a BundleUpgrade attached to
   * the treat, not as a parallel add-on row. */
  { id: "ch-vovo",       bakery: "Chéri",      name: "Iced Vovo Pain Suisse (6pc)",   description: "Pillowy pastry, raspberry jam and buttercream — Aussie nostalgia", price: 72, surfaces: ["addon"], addonEmoji: "🇦🇺", addonCategory: "table",     availableWith: "Go All Out (Chéri)" },

  /* ── Phase D4 proof-of-concept treats ──
   *
   * First migration of a TreatOption into the unified PRODUCTS table.
   * The matching client-side TreatOption lives in
   * src/components/order/orderData.ts under RAW_TREAT_MENUS["Chéri"]
   * and references this entry via `productId`. Drift between the
   * client TreatOption.price and the Product.price below is asserted
   * by `treat-product-link.test.ts` — a mismatch fails CI before the
   * change ships.
   *
   * Why Eye Candy Grand Gâteau: it's the canonical Chéri hero used
   * across PACKAGE_COMPOSITION_REFS at every team-size band, so
   * anything operating on it sees both surfaces. If the migration
   * pattern works here, the rest of the Chéri menu (and eventually
   * the other bakeries) follows the same shape.
   *
   * Treat-side fields (`treatCategory`, `treatServes`) are required
   * when surfaces includes "treat" — see validator. */
  {
    id: "ch-eye-candy-gateau",
    bakery: "Chéri",
    name: "Eye Candy Grand Gâteau",
    description: "Lychee mousse, pistachio and raspberry. Chéri's signature.",
    price: 95,
    surfaces: ["treat"],
    treatCategory: "Grand Gâteau",
    treatServes: "8–12",
  },
];

/* ─── Validation ───
 *
 * Run at module load. Throws if PRODUCTS has structural defects —
 * surfaces a config bug at the start of the process rather than at
 * checkout. Returns an empty array on success so the function can
 * also be called from tests for explicit assertions.
 *
 * Catches:
 *   - Duplicate id (would let two products write to the same picker key)
 *   - Empty surfaces[] (the product is unreachable)
 *   - Unknown surface value (typo guard once enums grow)
 *   - addonEmoji / addonCategory / availableWith missing when "addon"
 *     is in surfaces — the legacy CatalogueAddOn export requires them
 *   - treatCategory missing when "treat" is in surfaces — the picker
 *     needs a non-empty category to group by
 *   - Negative or zero price (corrupts margin calc)
 */
export function validateProductsCatalogue(products: Product[]): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();
  const seenBakeryName = new Set<string>();

  for (const p of products) {
    if (seenIds.has(p.id)) {
      errors.push(`duplicate id: ${p.id}`);
    } else {
      seenIds.add(p.id);
    }

    // Guard against the original duplication bug (same product, two
    // surfaces, two prices) by detecting a name+bakery collision.
    // Two entries with the same bakery+name would mean the customer
    // sees the same line under two ids — a different failure mode
    // than the historical TREAT_MENUS/ADD_ONS split, but equally bad.
    const bakeryNameKey = `${p.bakery}::${p.name}`;
    if (seenBakeryName.has(bakeryNameKey)) {
      errors.push(`duplicate (bakery, name): ${bakeryNameKey}`);
    } else {
      seenBakeryName.add(bakeryNameKey);
    }

    if (!p.surfaces || p.surfaces.length === 0) {
      errors.push(`empty surfaces: ${p.id}`);
    }
    for (const s of p.surfaces ?? []) {
      if (!PRODUCT_SURFACES.includes(s)) {
        errors.push(`unknown surface "${s}" on ${p.id}`);
      }
    }

    if (typeof p.price !== "number" || p.price <= 0) {
      errors.push(`invalid price (${p.price}) on ${p.id}`);
    }

    if (p.surfaces?.includes("addon")) {
      if (!p.addonEmoji)    errors.push(`missing addonEmoji on ${p.id}`);
      if (!p.addonCategory) errors.push(`missing addonCategory on ${p.id}`);
      if (!p.availableWith) errors.push(`missing availableWith on ${p.id}`);
    }
    if (p.surfaces?.includes("treat")) {
      if (!p.treatCategory) errors.push(`missing treatCategory on ${p.id}`);
    }
  }

  return errors;
}

// Module-load assertion. Bun + Vite + Deno all eval this at import
// time, so a structural defect surfaces during build/test, not at
// checkout. We don't ship to prod past a thrown init.
const PRODUCT_VALIDATION_ERRORS = validateProductsCatalogue(PRODUCTS);
if (PRODUCT_VALIDATION_ERRORS.length > 0) {
  throw new Error(
    `PRODUCTS catalogue is invalid:\n  - ${PRODUCT_VALIDATION_ERRORS.join("\n  - ")}`,
  );
}

/* ─── Derived view: ADD_ONS (back-compat) ───
 *
 * Reproduces the prior `Record<BakeryName, CatalogueAddOn[]>` shape so
 * every existing import (StepAddOns, StepReviewPay, getAddOnImage,
 * edge functions doing checkout validation, the price-check script)
 * keeps compiling unchanged.
 *
 * Order of products within each bakery is preserved (PRODUCTS array
 * order = display order on the add-ons step). The empty-bakery fallback
 * keeps the type Record-of-array intact for bakeries that currently
 * have no add-ons (none today, but keeps shape-stable for the future).
 */
function deriveAddOnsView(products: Product[]): Record<BakeryName, CatalogueAddOn[]> {
  const view: Record<BakeryName, CatalogueAddOn[]> = {
    "Cupcake Central": [],
    "Little Cupcakes": [],
    "Goldelucks":      [],
    "Le Yeahllow":     [],
    "Chéri":           [],
  };
  for (const p of products) {
    if (!p.surfaces.includes("addon")) continue;
    view[p.bakery].push({
      id: p.id,
      name: p.name,
      description: p.description,
      price: p.price,
      // Required-when-addon fields are guaranteed non-null by the
      // validator above; the `!` here documents that, doesn't mask it.
      emoji: p.addonEmoji!,
      category: p.addonCategory!,
      availableWith: p.availableWith!,
      ...(p.image ? { image: p.image } : {}),
    });
  }
  return view;
}

export const ADD_ONS: Record<BakeryName, CatalogueAddOn[]> = deriveAddOnsView(PRODUCTS);

/* ─── Disposable email domains blocklist ───
 * Small, maintained list. Real customers won't use these.
 */
export const DISPOSABLE_EMAIL_DOMAINS = new Set<string>([
  "mailinator.com",
  "10minutemail.com",
  "guerrillamail.com",
  "tempmail.com",
  "trashmail.com",
  "yopmail.com",
  "dispostable.com",
  "getnada.com",
  "maildrop.cc",
  "sharklasers.com",
  "temp-mail.org",
  "fakeinbox.com",
]);

// Matches client-side validation in StepYourDetails.tsx
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
